/**
 * ExploreCommunities Component
 * Search bar, location indicator, filter chips, and community grid with infinite scroll
 */

import { useQuery } from "@tanstack/react-query"
import { MapPin, Search, X } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"
import { AutocompleteInput } from "@/components/forms/autoCompleteInput"
import { formatLocation } from "@/components/shared/formatLocation"
import { RadioPopover } from "@/components/shared/RadioPopover"
import { ToggleChipPopover } from "@/components/shared/ToggleChipPopover"
import { useAuthStore } from "@/features/auth/store/authStore"
import { getIntentions, getSpiritualTraditions } from "@/features/profile/services/user.service"
import { useTranslation } from "@/language"
import type { ConnectFilters } from "../types/connect.types"
import { CommunityGrid } from "./CommunityGrid"

interface ExploreCommunitiesProps {
  communities: import("../types/connect.types").ConnectCommunity[]
  isLoading: boolean
  hasMore: boolean
  onLoadMore: () => void
  filters: ConnectFilters
  onFiltersChange: (filters: ConnectFilters) => void
  userCity?: string
  userState?: string
}

export function ExploreCommunities({
  communities,
  isLoading,
  hasMore,
  onLoadMore,
  filters,
  onFiltersChange,
  userCity,
  userState,
}: ExploreCommunitiesProps) {
  const { t } = useTranslation("common")
  const [searchInput, setSearchInput] = useState(filters.search)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const location = formatLocation(userCity, userState)
  const user = useAuthStore(state => state.user)
  const [locationDisplay, setLocationDisplay] = useState(location)

  const sortOptions = [
    { label: t("connect.filters.nearest"), value: "nearest" },
    { label: t("connect.filters.newest"), value: "newest" },
    { label: t("connect.filters.popular"), value: "popular" },
  ] as const

  const showOptions = [
    { label: t("connect.filters.local"), value: "local" },
    { label: t("connect.filters.all"), value: "all" },
  ] as const

  const updateFilters = useCallback(
    (update: Partial<ConnectFilters>) => {
      onFiltersChange({ ...filters, ...update })
    },
    [onFiltersChange, filters],
  )

  const handleLocationSelect = useCallback(
    (loc: { city?: string; state?: string; country?: string }) => {
      const display = formatLocation(loc.city ?? null, loc.state ?? null)
      setLocationDisplay(display)
      updateFilters({ city: loc.city, state: loc.state, country: loc.country })
    },
    [updateFilters],
  )

  const handleLocationClear = useCallback(() => {
    setLocationDisplay("")
    updateFilters({ city: undefined, state: undefined, country: undefined })
  }, [updateFilters])

  // (Popover state managed inside shared RadioPopover/ToggleChipPopover components)

  // Fetch intentions list
  const { data: intentionsData } = useQuery({
    queryKey: ["connect-filter-intentions", user?.id],
    queryFn: () => getIntentions({ page: 1, limit: 100, user_id: user?.id ?? "" }),
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  })

  // Fetch spiritual traditions list
  const { data: traditionsData } = useQuery({
    queryKey: ["connect-filter-spiritual-traditions"],
    queryFn: getSpiritualTraditions,
    staleTime: 5 * 60 * 1000,
  })

  const intentionsList = intentionsData?.intentions ?? []
  const traditionsList = traditionsData ?? []

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  const resetFilters = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
      debounceRef.current = null
    }
    setSearchInput("")
    setLocationDisplay(location)
    onFiltersChange({
      search: "",
      sortBy: "nearest",
      show: "local",
      intentions: [],
      spiritualTraditions: [],
      city: undefined,
      state: undefined,
      country: undefined,
    })
  }, [onFiltersChange, location])

  const hasActiveFilters =
    filters.search !== "" ||
    filters.sortBy !== "nearest" ||
    filters.show !== "local" ||
    filters.intentions.length > 0 ||
    filters.spiritualTraditions.length > 0 ||
    !!filters.city ||
    !!filters.state ||
    !!filters.country

  return (
    <section className="pb-10 md:py-12 max-w-360 mx-auto px-5 md:px-17.5">
      {/* Section Title */}
      <h2 className="text-xl font-semibold md:section-heading text-text-primary mb-5 md:mb-7 uppercase">
        {t("connect.exploreCommunities")}
      </h2>

      {/* Search Bar */}
      <div className="mb-5 md:mb-3">
        <div className="relative">
          <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-[25px] h-[25px] text-primary opacity-90" />
          <input
            type="text"
            placeholder={t("connect.searchPlaceholder")}
            value={searchInput}
            onChange={e => {
              const value = e.target.value
              setSearchInput(value)
              if (debounceRef.current) clearTimeout(debounceRef.current)
              debounceRef.current = setTimeout(() => {
                updateFilters({ search: value })
              }, 300)
            }}
            className="w-full h-[50px] md:h-[65px] pl-[49px] pr-5 md:pr-52 rounded-[15px] bg-[rgba(242,240,235,0.05)] border border-[rgba(242,240,235,0.15)] text-primary placeholder:text-primary/90 text-sm leading-[25px] focus:outline-none focus:ring-1 focus:ring-brand-olive"
            aria-label={t("connect.searchAria")}
          />
          {location && (
            <div className="hidden md:flex absolute right-5 top-1/2 -translate-y-1/2 items-center gap-1.25 text-primary text-sm font-semibold whitespace-nowrap opacity-90">
              <MapPin className="w-[25px] h-[25px]" />
              <AutocompleteInput
                label=""
                mode="full"
                placeholder={t("connect.locationPlaceholder")}
                value={locationDisplay}
                onLocationSelect={handleLocationSelect}
                className="h-auto min-h-0 p-0 border-0 text-primary text-sm font-semibold bg-transparent"
              />
              {(filters.city || filters.state || filters.country) && (
                <button
                  type="button"
                  onClick={handleLocationClear}
                  className="flex items-center justify-center w-4 h-4 rounded-full hover:bg-primary/20 transition-colors"
                  aria-label={t("connect.clearLocation")}
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Filter Pills */}
      <div className="flex items-center gap-[10px] mb-3.75 md:mb-6 overflow-x-auto scrollbar-hide md:pb-2">
        {/* Location -- mobile only */}
        {location && (
          <div className="shrink-0 md:hidden">
            <div className="flex items-center gap-1.25 text-primary text-sm font-semibold whitespace-nowrap opacity-90 px-3 h-[40px] rounded-[10px] border border-[#F2F0EB] p-[10px]">
              <MapPin className="w-4 h-4" />
              <AutocompleteInput
                label=""
                mode="full"
                placeholder={t("connect.locationPlaceholder")}
                value={locationDisplay}
                onLocationSelect={handleLocationSelect}
                className="h-auto min-h-0 p-0 border-0 text-primary text-sm font-semibold bg-transparent"
              />
              {(filters.city || filters.state || filters.country) && (
                <button
                  type="button"
                  onClick={handleLocationClear}
                  className="flex items-center justify-center w-4 h-4 rounded-full hover:bg-primary/20 transition-colors"
                  aria-label={t("connect.clearLocation")}
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>
        )}

        <div className="shrink-0">
          <RadioPopover
            label={t("connect.filters.sortBy")}
            options={sortOptions}
            value={filters.sortBy}
            onChange={v => updateFilters({ sortBy: v as ConnectFilters["sortBy"] })}
          />
        </div>

        <div className="shrink-0">
          <RadioPopover
            label={t("connect.filters.show")}
            options={showOptions}
            value={filters.show}
            onChange={v => updateFilters({ show: v as ConnectFilters["show"] })}
          />
        </div>

        <div className="shrink-0">
          <ToggleChipPopover
            label={t("connect.filters.intention")}
            options={intentionsList.map(i => ({ value: i.id, label: i.title }))}
            selected={filters.intentions}
            onApply={vals => updateFilters({ intentions: vals })}
          />
        </div>

        <div className="shrink-0">
          <ToggleChipPopover
            label={t("connect.filters.spiritualTraditions")}
            options={traditionsList.map(t => ({ value: t.id, label: t.title }))}
            selected={filters.spiritualTraditions}
            onApply={vals => updateFilters({ spiritualTraditions: vals })}
            maxWidth="480px"
          />
        </div>

        {hasActiveFilters && (
          <div className="shrink-0">
            <button
              type="button"
              onClick={resetFilters}
              className="flex items-center gap-[5px] pl-[10px] text-[14px] font-semibold text-brand-terracotta hover:text-brand-terracotta/80 transition-colors"
            >
              <span className="size-[20px] rounded-full border border-brand-terracotta flex items-center justify-center">
                <X className="size-[12px]" />
              </span>
              {t("connect.filters.resetAll")}
            </button>
          </div>
        )}
      </div>

      {/* Search Results Count */}
      {filters.search && (
        <p className="text-[14px] text-white opacity-80 leading-[25px] md:mt-5">
          {communities.length === 1
            ? t("connect.resultsCount", { count: communities.length, query: filters.search })
            : t("connect.resultsCount_plural", {
                count: communities.length,
                query: filters.search,
              })}
        </p>
      )}

      {/* Community Grid */}
      <CommunityGrid
        communities={communities}
        isLoading={isLoading}
        hasMore={hasMore}
        onLoadMore={onLoadMore}
      />
    </section>
  )
}
